import type { MessageDefinition } from "../messaging/messenger"

export type DataResourceId = string | number

type DataDependencyScope = "all" | "collection" | "item"

export interface DataDependency {
  readonly resource: symbol
  readonly resourceName: string
  readonly scope: DataDependencyScope
  readonly id?: DataResourceId
}

export type DataDependencyInput = DataDependency | readonly DataDependency[]

export interface DataResource<
  TItemId extends DataResourceId = DataResourceId,
  TCollectionId extends DataResourceId = TItemId,
> {
  readonly name: string
  /** Observe any change in the resource; invalidating it broadcasts to every dependency. */
  readonly all: DataDependency
  /** One logical record in the resource. */
  item(id: TItemId): DataDependency
  /** One structural collection/view of the resource, such as the rows for a stockId. */
  collection(id: TCollectionId): DataDependency
}

export interface DataInvalidationEvent {
  readonly revision: number
  readonly dependencies: readonly DataDependency[]
}

export interface DataInvalidation {
  invalidate(...dependencies: readonly DataDependencyInput[]): void
  revision(...dependencies: readonly DataDependencyInput[]): number
  subscribe(listener: (event: DataInvalidationEvent) => void): () => void
}

export interface DataInvalidationRegistrar {
  /**
   * Bind a typed application/domain message to data invalidation without making
   * the shared message definition depend on a feature-owned read model.
   */
  invalidateOn<TPayload>(
    message: MessageDefinition<TPayload>,
    invalidates: DataDependencyInput | ((payload: Readonly<TPayload>) => DataDependencyInput)
  ): () => void
}

interface ResourceRevisionState {
  aggregate: number
  broadcast: number
  readonly exact: Map<string, number>
}

export function defineDataResource<
  TItemId extends DataResourceId = DataResourceId,
  TCollectionId extends DataResourceId = TItemId,
>(name: string): DataResource<TItemId, TCollectionId> {
  if (name.trim().length === 0) throw new Error("Data resource name cannot be empty.")
  const resource = Symbol(name)
  const all = createDependency(resource, name, "all")
  return Object.freeze({
    name,
    all,
    item: (id: TItemId) => createDependency(resource, name, "item", id),
    collection: (id: TCollectionId) => createDependency(resource, name, "collection", id),
  })
}

export function createDataInvalidation(): DataInvalidation {
  let revision = 0
  const resources = new Map<symbol, ResourceRevisionState>()
  const listeners = new Set<(event: DataInvalidationEvent) => void>()

  const stateFor = (dependency: DataDependency) => {
    let state = resources.get(dependency.resource)
    if (state == null) {
      state = { aggregate: 0, broadcast: 0, exact: new Map() }
      resources.set(dependency.resource, state)
    }
    return state
  }

  return Object.freeze({
    invalidate(...inputs: readonly DataDependencyInput[]) {
      const dependencies = uniqueDependencies(inputs.flatMap(normalizeDataDependencies))
      if (dependencies.length === 0) return
      const nextRevision = ++revision
      for (const dependency of dependencies) {
        const state = stateFor(dependency)
        state.aggregate = nextRevision
        if (dependency.scope === "all") state.broadcast = nextRevision
        else state.exact.set(dataDependencyKey(dependency), nextRevision)
      }
      const event = Object.freeze({
        revision: nextRevision,
        dependencies: Object.freeze(dependencies.slice()),
      })
      let firstError: unknown
      for (const listener of listeners) {
        try {
          listener(event)
        } catch (error) {
          firstError ??= error
        }
      }
      if (firstError !== undefined) throw firstError
    },
    revision(...inputs: readonly DataDependencyInput[]) {
      let current = 0
      for (const dependency of inputs.flatMap(normalizeDataDependencies)) {
        const state = resources.get(dependency.resource)
        if (state == null) continue
        current = Math.max(
          current,
          dependency.scope === "all"
            ? state.aggregate
            : Math.max(state.broadcast, state.exact.get(dataDependencyKey(dependency)) ?? 0)
        )
      }
      return current
    },
    subscribe(listener: (event: DataInvalidationEvent) => void) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  } satisfies DataInvalidation)
}

export function normalizeDataDependencies(input: DataDependencyInput | undefined): readonly DataDependency[] {
  if (input == null) return []
  if (Array.isArray(input)) return input as readonly DataDependency[]
  return [input as DataDependency]
}

/** @internal */
export function dataDependencyKey(dependency: DataDependency) {
  return dependency.scope === "all" ? "all" : `${dependency.scope}:${typeof dependency.id}:${String(dependency.id)}`
}

/** @internal */
export function dataDependencyEquals(left: DataDependency, right: DataDependency) {
  return left.resource === right.resource && dataDependencyKey(left) === dataDependencyKey(right)
}

/**
 * Whether an explicit invalidation affects a dependency. `all` is an aggregate
 * dependency when observed and a broadcast dependency when invalidated.
 */
export function dataInvalidationAffects(dependency: DataDependency, invalidated: DataDependency) {
  if (dependency.resource !== invalidated.resource) return false
  if (dependency.scope === "all" || invalidated.scope === "all") return true
  return dataDependencyEquals(dependency, invalidated)
}

function createDependency(
  resource: symbol,
  resourceName: string,
  scope: DataDependencyScope,
  id?: DataResourceId
): DataDependency {
  return Object.freeze({ resource, resourceName, scope, id })
}

function uniqueDependencies(dependencies: readonly DataDependency[]) {
  const result: DataDependency[] = []
  for (const dependency of dependencies) {
    if (!result.some((current) => dataDependencyEquals(current, dependency))) result.push(dependency)
  }
  return result
}
